import { getCampaignAttribution } from "./campaignAttribution";
import { trackSavedLead } from "./marketingTracking";
import { initializeApp } from "firebase/app";
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from "firebase/app-check";
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from "firebase/functions";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const launchListEnabled =
  import.meta.env.VITE_LAUNCH_LIST_ENABLED === "true" ||
  import.meta.env.VITE_USE_EMULATORS === "true";
const usingEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;

let functions: Functions | null = null;

if (firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId) {
  const app = initializeApp(firebaseConfig, "envelaro-public");
  functions = getFunctions(
    app,
    import.meta.env.VITE_FUNCTIONS_REGION || "europe-west1",
  );
  if (usingEmulators) {
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  } else if (recaptchaSiteKey && typeof document !== "undefined") {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(
        recaptchaSiteKey,
      ),
      isTokenAutoRefreshEnabled: true,
    });
  }
}

export type EmailHealthStatus = "passed" | "warning" | "critical";
export type EmailHealthFindingType =
  | "passed"
  | "failed"
  | "unverified"
  | "recommended"
  | "optional";
export type EmailHealthRisk =
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "informational";
export type EmailHealthCategory =
  | "Mail delivery"
  | "Sender authorization"
  | "Anti-spoofing"
  | "Transport & reporting"
  | "DNS & brand trust";

export type EmailHealthResult = {
  domain: string;
  checkedAt: string;
  grade: "A" | "B" | "C" | "D";
  score: number;
  mailProvider: string | null;
  dnsProvider: string | null;
  dkimSelector: string | null;
  checks: Array<{
    id: string;
    category: EmailHealthCategory;
    title: string;
    status: EmailHealthStatus;
    summary: string;
    detail: string;
    recommendation: string;
    affectsGrade: boolean;
    findingType: EmailHealthFindingType;
    risk: EmailHealthRisk;
    penalty: number;
    evidence: Array<{
      query: string;
      type: "TXT" | "MX" | "NS" | "CAA";
      records: string[];
      ttlSeconds: number | null;
      lookupMs: number;
      resolver: string;
      responseStatus: string;
      dnssecValidated: boolean;
      verified: boolean;
      note: string | null;
    }>;
  }>;
  scoreSummary: {
    startingScore: 100;
    deductions: Array<{
      checkId: string;
      title: string;
      points: number;
      reason: string;
    }>;
    finalScore: number;
  };
  remediation: Array<{
    checkId: string;
    priority: number;
    title: string;
    owner: "Email administrator" | "DNS administrator" | "Security team" | "Web administrator";
    effort: "Low" | "Medium" | "High";
    risk: EmailHealthRisk;
    recordName: string | null;
    recordType: "TXT" | "MX" | "NS" | "CAA" | null;
    copyReadyValue: string | null;
    caution: string;
  }>;
  evidenceSource: string;
};

export const emailHealthEnabled =
  Boolean(functions) && (usingEmulators || Boolean(recaptchaSiteKey));

const WEBSITE_VISIT_SESSION_KEY = "envelaro:website-visit:v1";

export async function recordWebsiteVisit() {
  if (!functions || typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(WEBSITE_VISIT_SESSION_KEY)) return;
    window.sessionStorage.setItem(WEBSITE_VISIT_SESSION_KEY, "recording");
  } catch {
    return;
  }

  try {
    await httpsCallable<Record<string, never>, { recorded: boolean }>(
      functions,
      "recordWebsiteVisit",
    )({});
    window.sessionStorage.setItem(WEBSITE_VISIT_SESSION_KEY, "recorded");
  } catch {
    window.sessionStorage.removeItem(WEBSITE_VISIT_SESSION_KEY);
  }
}

export async function scanPublicEmailDns(data: {
  domain: string;
  dkimSelector?: string;
}) {
  if (!emailHealthEnabled || !functions) {
    throw new Error("The email health check is temporarily unavailable.");
  }
  const callable = httpsCallable<typeof data, EmailHealthResult>(
    functions,
    "scanPublicEmailDns",
  );
  return (await callable(data)).data;
}

export async function submitLaunchLead(data: {
  name: string;
  email: string;
  company: string;
  teamSize: string;
  currentProvider: string;
  message: string;
  consent: true;
  privacyVersion: string;
  source: "website_contact" | "email_health_check";
  leadType?: "general" | "email_health_check" | "managed_private_deployment";
  deploymentRequirements?: {
    deploymentModel: string;
    domains: string;
    storageNeed: string;
    preferredRegion: string;
    identityProvider: string;
    timeline: string;
    securityRequirements: string;
    migrationRequirements: string;
  };
  scannedDomain?: string;
  dkimSelector?: string;
  website: string;
  elapsedMs: number;
}) {
  if (!launchListEnabled) {
    throw new Error("The enquiry form is unavailable.");
  }
  if (!functions) throw new Error("The enquiry form is temporarily unavailable.");
  const result = await httpsCallable<Record<string, unknown>, { accepted: boolean; conversionEventId?: string }>(functions, "submitLaunchLead")({ ...data, privacyVersion: "website-privacy-2026-09-09", attribution: getCampaignAttribution() });
  // Measurement must never turn a saved enquiry into a submission error.
  try { if (result.data.conversionEventId) trackSavedLead(result.data.conversionEventId); } catch { /* Optional measurement only. */ }
}
